import {
  Box,
  Drawer,
  DrawerContent,
  Flex,
  FlexProps,
  HStack,
  IconButton,
  Skeleton,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useConst,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { FiCalendar, FiMenu } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';

import StyledMessageList from '../../components/StyledMessageList';
import Transitions from '../../components/Transitions';
import { pagePaths } from '../../constants/Page';
import { useEngeneNavigate } from '../../hooks/navigate';
import { DeadlineSearchResultResults } from '../../models/gen/deadlineSearchResultResults';
import {
  Display,
  Height,
  MarginLeft,
  OnClickButton,
  Padding,
  PaddingX,
  Width,
} from '../../models/properties';
import PersonalMenu from './PersonalMenu';
import SidebarContent from './Sidebar';

type WireframeProps = React.PropsWithChildren<{
  title: string;
  guarding?: boolean;
  backdropMessage?: string;
  topMessages?: Array<DeadlineSearchResultResults>;
  messageClickDisabled?: boolean;
  isLoaded?: boolean;
}>;

const Wireframe: React.FunctionComponent<WireframeProps> = ({
  title,
  children,
  topMessages = [],
  isLoaded = true,
  messageClickDisabled = false,
  /* eslint-disable @typescript-eslint/no-unused-vars */
  guarding,
  backdropMessage,
  /* eslint-enable @typescript-eslint/no-unused-vars */
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  console.debug(`${location.pathname}${location.search}`);

  const sideBarContentDisplay = useBreakpointValue<Display>({
    base: 'none',
    md: 'block',
  });
  const bodyHeight = useBreakpointValue<Height>({
    base: 'calc(95% - 12 - 16px)',
    md: 'calc(95% - 20 - 16px)',
  });
  const bodyMarginLeft = useBreakpointValue<MarginLeft>({
    base: 0,
    md: 60,
  });
  const bodyPadding = useBreakpointValue<Padding>({
    base: 2,
    md: 4,
  });
  const bodyWidth = useBreakpointValue<Width>({
    base: 'calc(100vw - 24px)',
    md: 'calc(100vw - var(--chakra-sizes-60) - 24px)',
  });

  return (
    <Box
      background={useColorModeValue('gray.100', 'gray.900')}
      className="wireframe"
      height="100%"
      // maxWidth="100%"
      width="100%"
    >
      <SidebarContent
        display={sideBarContentDisplay}
        onClose={onClose}
        pathname={location.pathname}
      />
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        onClose={onClose}
        onOverlayClick={onClose}
        placement="left"
        returnFocusOnClose={false}
        size="full"
      >
        <DrawerContent>
          <SidebarContent onClose={onClose} pathname={location.pathname} />
        </DrawerContent>
      </Drawer>
      {/* mobilenav */}
      <MobileNav className="mobile-nav" onOpen={onOpen} title={title} />
      <Transitions>
        <VStack
          className="main-area"
          height={bodyHeight}
          marginLeft={bodyMarginLeft}
          overflow="auto"
          padding={bodyPadding}
          width={bodyWidth}
        >
          <Skeleton as={VStack} isLoaded={isLoaded} width="full">
            <StyledMessageList
              array={topMessages}
              hasLink={!messageClickDisabled}
            />
            {children}
          </Skeleton>
        </VStack>
      </Transitions>
    </Box>
  );
};

interface MobileProps extends FlexProps {
  onOpen: () => void;
  title: string;
}
const MobileNav: React.FunctionComponent<MobileProps> = ({
  onOpen,
  title,
  ...rest
}) => {
  const navigate = useEngeneNavigate();

  const flexHeight = useBreakpointValue<Height>({
    base: 12,
    md: 20,
  });
  const flexMarginLeft = useBreakpointValue<MarginLeft>({
    base: 0,
    md: 60,
  });
  const flexPaddingX = useBreakpointValue<PaddingX>({
    base: 0,
    md: 4,
  });

  const menuIconVisibility = useBreakpointValue<
    React.CSSProperties['visibility']
  >({ md: 'hidden' });
  const onClickDailyOperation = React.useCallback<OnClickButton>(
    () => navigate(pagePaths.DAILY_OPERATION),
    []
  );

  return (
    <Flex
      alignItems="center"
      background={useColorModeValue('white', 'gray.900')}
      borderBottomWidth="1px"
      borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
      height={flexHeight}
      justifyContent="space-between"
      marginLeft={flexMarginLeft}
      paddingX={flexPaddingX}
      pos="sticky"
      top={0}
      zIndex={100}
      {...rest}
    >
      <HStack display="flex" visibility={menuIconVisibility}>
        <IconButton
          onClick={onOpen}
          variant="outline"
          aria-label="open menu"
          icon={<FiMenu />}
        />
        <IconButton
          onClick={onClickDailyOperation}
          variant="outline"
          aria-label="calendar"
          icon={<FiCalendar />}
        />
      </HStack>

      <Text display="flex" fontSize="2xl" fontWeight="bold">
        {/* Logo */}
        {title}
      </Text>

      <PersonalMenu />
    </Flex>
  );
};

export default Wireframe;
